import {Channel} from "@hosttools/core/shared/model/channel";
import type {InfiniteData} from "@tanstack/react-query";
import {useInfiniteQuery, useQuery} from "@tanstack/react-query";
import {isWithinInterval} from "date-fns";
import orderBy from "lodash/orderBy";
import {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";

import type {Reservation, ReservationPost, ReservationRaw} from "../../../models/reservation";
import {FrontendContext} from "../../contexts/Frontend";
import useDebounceFn from "../useDebounceFn";
import useFetchTimelines from "../useFetchTimelines";
import {useReservationService} from "../useReservationService";

import type {Post} from "./share";
import {buildPosts, postFillerPrefix} from "./share";

const defaultPostsOffset = 20;

interface Props {
    reservation: Reservation | ReservationRaw | undefined | null;
    showTimeline: boolean;
    order?: "asc" | "desc";
    isDemo?: boolean;
}

const useReservationPosts = ({reservation, showTimeline, order = "asc", isDemo = false}: Props) => {
    const {baseUrl} = useContext(FrontendContext);
    const [posts, setPosts] = useState<Post[] | undefined>(undefined);
    const {_id: reservationID, source} = reservation ?? {};
    const reservationService = useReservationService();

    const {
        timelines,
        messages: sentMessages,
        isLoading: isLoadingTimelines,
        removeTimelines,
        removeMessages
    } = useFetchTimelines(reservationID, true, isDemo);

    const {data: channelThread, isLoading: isThreadLoading} = useQuery({
        // only support to fetch thread for `Airbnb` for now
        enabled: source === Channel.Airbnb && !!reservationID,
        queryKey: ["/getThread/:reservationID", reservationID],
        queryFn: async () => {
            return reservationService.fetchThread(
                reservationID ?? "",
                undefined,
                undefined,
                isDemo
            );
        }
    });

    const offsetRef = useRef(0);
    const {
        data,
        isLoading: isLoadingPosts,
        isFetchingNextPage,
        refetch,
        hasNextPage,
        fetchNextPage
    } = useInfiniteQuery<
        ReservationPost[],
        Error,
        InfiniteData<ReservationPost[]>,
        readonly unknown[],
        number
    >({
        enabled: source === Channel.Airbnb ? !isThreadLoading : !!reservationID,
        queryKey: ["/reservation/posts/:reservationID", reservationID],
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages, lastPageParam) => {
            if (lastPage.length === 0) {
                return undefined;
            }
            return lastPageParam + defaultPostsOffset;
        },
        queryFn: async ({pageParam}) => {
            offsetRef.current = pageParam;
            const result = await reservationService.get(
                "/reservation/posts/:reservationID",
                {
                    reservationID: reservationID ?? ""
                },
                {
                    limit: defaultPostsOffset,
                    offset: pageParam,
                    demo: isDemo ? "true" : undefined
                }
            );
            return result.docs;
        }
    });

    const addPosts = useCallback(
        (posts: ReservationPost[]) => {
            // added posts and wait for the update
            if (reservationID && source) {
                setPosts(prev => {
                    // Prevent add new pending message when new message is already updated by ws
                    if (prev && posts.length === 1 && posts[0]._id.startsWith(postFillerPrefix)) {
                        const lastPost = prev[0];
                        if (lastPost?.post.data.message === posts[0].message) {
                            return prev;
                        }
                    }
                    return [...(prev ?? []), ...buildPosts(reservationID, source, baseUrl, posts)];
                });
            }
        },
        [reservationID, source, baseUrl]
    );

    const removePosts = useCallback(
        (postIds: string[], type: Post["post"]["type"]) => {
            if (type === "timeline") {
                removeTimelines(postIds);
            } else if (type === "message") {
                removeMessages(postIds);
            } else {
                setPosts(prev => {
                    if (prev) {
                        return prev.filter(post => !postIds.includes(post.id));
                    }
                    return prev;
                });
            }
        },
        [removeTimelines, removeMessages]
    );

    useEffect(() => {
        if (reservationID && source && data) {
            const posts = data.pages.flat();
            const postsWithAttachments = buildPosts(reservationID, source, baseUrl, posts);
            setPosts(postsWithAttachments);
        }
    }, [reservationID, source, baseUrl, data]);

    const debounceRefetch = useDebounceFn(() => {
        refetch();
    }, 500);

    useEffect(() => {
        // sometimes it comes with bunch of updates at the same time
        if (reservation?.posts) {
            debounceRefetch();
        }
    }, [reservation, debounceRefetch]);

    const isLoading = isLoadingPosts || isThreadLoading || isLoadingTimelines;

    const showingTimelines = useMemo(
        () =>
            timelines?.filter(
                timeline =>
                    timeline.status !== "sent" || timeline.emailEnabled || timeline.smsEnabled
            ),
        [timelines]
    );
    const sortedPosts = useMemo<Post[] | undefined>(() => {
        if (!posts) {
            return;
        }

        const sentMessagesDisplay = sentMessages?.filter(elem => {
            // ignore the unknown date which seems to be incorrect data
            if (!elem.failedDate && !elem.sentDate) {
                return false;
            }
            const timeline = timelines?.find(timeline => {
                return timeline.messageRuleID === elem.messageRuleID;
            });
            if (timeline) {
                return false;
            }

            if (hasNextPage) {
                const sortedPosts = orderBy(posts, "date", "desc");
                const firstPost = sortedPosts[0];
                const lastPost = sortedPosts[posts.length - 1];
                const messageDate = elem.failedDate || elem.sentDate;
                if (
                    !messageDate ||
                    !isWithinInterval(new Date(messageDate), {
                        start: new Date(lastPost.date),
                        end: new Date(firstPost.date)
                    })
                ) {
                    return false;
                }
            }
            return true;
        });
        const allTypesMessages = [...(posts ?? [])];

        if (sentMessagesDisplay) {
            const filteredsentMessages = sentMessagesDisplay.map<Post>(message => ({
                id: message._id,
                date: message.failedDate || message.sentDate || "",
                post: {
                    type: "message",
                    data: message,
                    role: "host"
                }
            }));

            allTypesMessages.push(...filteredsentMessages);
        }

        if (showTimeline) {
            const filteredScheduleTimelines = showingTimelines
                ? showingTimelines.filter(timeline => timeline.failedDate === undefined)
                : [];
            const filteredTimelines = filteredScheduleTimelines.map<Post>(timeline => ({
                id: timeline._id,
                date: timeline.sentDate || timeline.sendDate,
                post: {
                    type: "timeline",
                    data: timeline,
                    role: "host"
                }
            }));
            allTypesMessages.push(...filteredTimelines);
        }

        if (timelines) {
            const failedTimelines = timelines.reduce<Post[]>((arr, timeline) => {
                if (timeline.failedDate) {
                    arr.push({
                        id: timeline._id,
                        date: timeline.failedDate,
                        post: {
                            type: "timeline",
                            data: timeline,
                            role: "host"
                        }
                    });
                }
                return arr;
            }, []);

            allTypesMessages.push(...failedTimelines);
        }

        return orderBy(allTypesMessages, "date", order);

        // ideally we should render everything at once to avoid flashing ...
    }, [order, showTimeline, posts, sentMessages, timelines, hasNextPage, showingTimelines]);

    return {
        sortedPosts,
        offset: offsetRef.current,
        hasNextPage,
        timelines,
        showingTimelines,
        sentMessages,
        channelThread,
        isLoading,
        isLoadingPosts,
        isLoadingTimelines,
        isFetchingNextPage,
        setPosts,
        addPosts,
        removePosts,
        refetch,
        fetchNextPage
    };
};

export default useReservationPosts;
